import { useCallback, useEffect, useState } from "react";

import { verDesglose, type Desglose as DatosDesglose, type FilaDesglose, type Yo } from "./api";
import { Cargando, Cifra, ErrorDeCarga, Insignia, Tabla, type Columna } from "./componentes";
import { numero, porcentaje } from "./formato";
import { mensajeDeError } from "./mensajes";
import type { Navegar } from "./navegacion";

/**
 * EL DESGLOSE: un territorio en sus hijos, con migas de pan y el total.
 *
 * Es la tabla del centro de mando de la v1 traída a Brújula: por hijo, lista
 * nominal, personas, cobertura (% de la lista que ya está en el padrón),
 * verificados, votos para ganar y avance. Tocar una fila baja un nivel; la
 * mira abre el mapa encuadrado en ese territorio. El total se suma de las
 * filas, no con otra consulta: arriba y abajo cuadran siempre.
 *
 * Lo de fuera del ámbito se dice con palabras y no con un cero.
 */
export function Desglose({
  yo,
  nodoInicial,
  navegar,
  compacto = false,
}: {
  yo: Yo;
  nodoInicial: number | null;
  navegar: Navegar;
  compacto?: boolean;
}) {
  const [nodo, setNodo] = useState<number | null>(nodoInicial);
  const [datos, setDatos] = useState<DatosDesglose | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNodo(nodoInicial);
  }, [nodoInicial]);

  const cargar = useCallback(async (n: number | null) => {
    setError(null);
    try {
      setDatos(await verDesglose(n));
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }, []);

  useEffect(() => {
    void cargar(nodo);
  }, [nodo, cargar]);

  if (error) return <ErrorDeCarga mensaje={error} reintentar={() => void cargar(nodo)} />;
  if (!datos) return <Cargando que="el desglose" />;

  const TIPO: Record<string, string> = {
    estado: "Estado", municipio: "Municipio", distrito: "Distrito local", zona: "Zona", seccion: "Sección",
  };
  const t = datos.total;
  const conMetas = datos.metas_visibles;
  const conEstructura = datos.estructura_visible;

  const columnas: Columna<FilaDesglose>[] = [
    {
      id: "nombre",
      titulo: "Territorio",
      clave: (f) => f.nombre,
      celda: (f) => (
        <>
          <span className="apagado chico">{TIPO[f.tipo] ?? f.tipo}</span>
          <br />
          <strong className={f.tipo === "seccion" ? "usuario" : undefined}>{f.nombre}</strong>
          {!f.en_ambito && (
            <>
              {" "}
              <Insignia tono="neutro">fuera de tu ámbito</Insignia>
            </>
          )}
          {f.tiene_hijos && <span className="apagado chico"> ›</span>}
        </>
      ),
    },
    {
      id: "ln",
      titulo: "Lista nominal",
      numero: true,
      clave: (f) => f.lista_nominal ?? -1,
      celda: (f) => (f.en_ambito ? numero(f.lista_nominal) : "—"),
    },
    { id: "personas", titulo: "Personas", numero: true, clave: (f) => f.personas, celda: (f) => (f.en_ambito ? numero(f.personas) : "—") },
    {
      id: "cobertura",
      titulo: "Cobertura",
      numero: true,
      clave: (f) => f.cobertura_pct ?? -1,
      celda: (f) => (f.en_ambito && f.cobertura_pct != null ? `${f.cobertura_pct} %` : "—"),
    },
    {
      id: "verificados",
      titulo: "Verificados",
      numero: true,
      clave: (f) => f.verificados,
      celda: (f) => (f.en_ambito ? numero(f.verificados) : "—"),
    },
    ...(conMetas
      ? ([
          {
            id: "meta",
            titulo: "Votos para ganar",
            numero: true,
            clave: (f) => f.meta_votos ?? -1,
            celda: (f) =>
              !f.en_ambito ? "—" : f.meta_votos == null ? <span className="apagado">{f.tipo === "seccion" ? (f.meta_origen === "sin_base" ? "sin base" : "—") : "—"}</span> : numero(f.meta_votos),
          },
          {
            id: "avance",
            titulo: "Avance",
            numero: true,
            clave: (f) => f.avance_pct ?? -1,
            celda: (f) => {
              if (!f.en_ambito || f.avance_pct == null) return "—";
              const tono = f.avance_pct >= 80 ? "exito" : f.avance_pct >= 50 ? "aviso" : "peligro";
              return <Insignia tono={tono}>{f.avance_pct} %</Insignia>;
            },
          },
        ] as Columna<FilaDesglose>[])
      : []),
    {
      id: "captura",
      titulo: "Con captura",
      numero: true,
      clave: (f) => (f.secciones_en_ambito ? f.con_captura / f.secciones_en_ambito : -1),
      celda: (f) =>
        f.tipo === "seccion" ? (
          f.en_ambito ? (f.con_captura ? <Insignia tono="exito">sí</Insignia> : <Insignia tono="peligro">no</Insignia>) : "—"
        ) : f.secciones_en_ambito === 0 ? (
          <span className="apagado">fuera de tu ámbito</span>
        ) : (
          <>
            {numero(f.con_captura)} / {numero(f.secciones_en_ambito)}
            <br />
            <span className="apagado chico">{porcentaje(f.con_captura, f.secciones_en_ambito)}</span>
          </>
        ),
    },
    ...(conEstructura
      ? ([
          {
            id: "responsable",
            titulo: "Responsable",
            numero: true,
            clave: (f) => (f.secciones_en_ambito ? f.con_responsable / f.secciones_en_ambito : -1),
            celda: (f) =>
              f.tipo === "seccion" ? (
                f.en_ambito ? (f.con_responsable ? <Insignia tono="exito">sí</Insignia> : <Insignia tono="peligro">no</Insignia>) : "—"
              ) : f.secciones_en_ambito === 0 ? (
                "—"
              ) : (
                <>
                  {numero(f.con_responsable)} / {numero(f.secciones_en_ambito)}
                  <br />
                  <span className="apagado chico">{porcentaje(f.con_responsable, f.secciones_en_ambito)}</span>
                </>
              ),
          },
        ] as Columna<FilaDesglose>[])
      : []),
    {
      id: "pendientes",
      titulo: "Bandeja",
      numero: true,
      clave: (f) => f.pendientes,
      celda: (f) => (f.pendientes > 0 ? <Insignia tono="aviso">{numero(f.pendientes)}</Insignia> : f.en_ambito ? "0" : "—"),
    },
    {
      id: "acciones",
      titulo: "Acciones",
      sinTitulo: true,
      celda: (f) => (
        <div className="acciones">
          <button
            type="button"
            className="btn terciario fila"
            title={`Ver ${f.nombre} en el mapa`}
            onClick={(e) => {
              e.stopPropagation();
              navegar({
                pantalla: "mapa",
                parametros: f.tipo === "seccion" ? { seccion: f.seccion, capa: "cobertura" } : { nodo: f.id, capa: "cobertura" },
              });
            }}
          >
            Mapa
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="desglose">
      <nav className="migas" aria-label="Dónde estás en el territorio">
        <button type="button" className="btn terciario fila" onClick={() => setNodo(null)} aria-current={nodo == null ? "page" : undefined}>
          {yo.alcance.todo ? "Toda la campaña" : "Tu ámbito"}
        </button>
        {datos.migas.map((m, i) => (
          <span key={m.id}>
            <span className="apagado" aria-hidden="true">
              {" › "}
            </span>
            {i === datos.migas.length - 1 ? (
              <strong>{m.nombre || `${m.tipo} ${m.clave}`}</strong>
            ) : (
              <button type="button" className="btn terciario fila" onClick={() => setNodo(m.id)}>
                {m.nombre || `${m.tipo} ${m.clave}`}
              </button>
            )}
          </span>
        ))}
      </nav>

      {!compacto && (
        <div className="cifras">
          <Cifra
            etiqueta="personas"
            valor={numero(t.personas)}
            detalle={`en ${numero(t.hogares)} hogares`}
            sinAccion="La suma de las filas de abajo"
          />
          <Cifra
            etiqueta="lista nominal"
            valor={t.lista_nominal != null ? numero(t.lista_nominal) : "—"}
            detalle={t.cobertura_pct != null ? `${t.cobertura_pct} % ya en el padrón` : "sin dato de la plataforma"}
            sinAccion="El electorado de estas secciones (INE)"
          />
          <Cifra
            etiqueta="verificados"
            valor={numero(t.verificados)}
            detalle={porcentaje(t.verificados, t.personas) + " del padrón"}
            sinAccion="Captura aprobada o identidad confirmada"
          />
          {conMetas && (
            <Cifra
              etiqueta="votos para ganar"
              valor={t.meta_votos != null ? numero(t.meta_votos) : "—"}
              detalle={t.avance_pct != null ? `${t.avance_pct} % de avance con el padrón` : "sin meta calculable aquí"}
              tono={t.avance_pct == null ? "neutro" : t.avance_pct >= 80 ? "exito" : t.avance_pct >= 50 ? "aviso" : "peligro"}
              alAbrir={() => navegar({ pantalla: "supuestos" })}
            />
          )}
          <Cifra
            etiqueta="secciones con captura"
            valor={`${numero(t.con_captura)} de ${numero(t.secciones_en_ambito)}`}
            detalle={porcentaje(t.con_captura, t.secciones_en_ambito)}
            alAbrir={() => navegar({ pantalla: "mapa", parametros: { ...(nodo != null ? { nodo } : {}), capa: "cobertura" } })}
          />
          {conEstructura && (
            <Cifra
              etiqueta="secciones con responsable"
              valor={`${numero(t.con_responsable)} de ${numero(t.secciones_en_ambito)}`}
              detalle={porcentaje(t.con_responsable, t.secciones_en_ambito)}
              tono={!t.secciones_en_ambito ? "neutro" : t.con_responsable === t.secciones_en_ambito ? "exito" : t.con_responsable * 2 >= t.secciones_en_ambito ? "aviso" : "peligro"}
              alAbrir={() => navegar({ pantalla: "mapa", parametros: { ...(nodo != null ? { nodo } : {}), capa: "responsable" } })}
            />
          )}
        </div>
      )}

      <Tabla
        columnas={columnas}
        filas={datos.filas}
        claveDe={(f) => f.id}
        caption="Cada territorio que cuelga de aquí con su lista nominal, su padrón y su meta. Toca un renglón para bajar un nivel."
        ordenInicial={{ columna: "personas", desc: true }}
        claseDeFila={(f) => (f.tiene_hijos ? "tocable" : "")}
        alTocarFila={(f) => {
          if (f.tiene_hijos) setNodo(f.id);
          else if (f.tipo === "seccion" && f.seccion) navegar({ pantalla: "mapa", parametros: { seccion: f.seccion, capa: "cobertura" } });
        }}
        vacio="Este territorio no se desglosa más."
      />
    </div>
  );
}
